// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025 Collab Digital Twins

// `import type` only: this reducer loads on every route, so a value import leaks @thatopen.
import type { Plan } from "../../types/bim";
import type { Building, DbFile } from '../../types/dbTypes';
import type { ActionMap } from "../ActionMap";
import type * as OBC from "@thatopen/components";

interface BimTypes {
    bimComponents: OBC.Components | null;
    world: OBC.World | null;
    fragments: OBC.FragmentsManager | null;
    modelId: string | null;
    /**
     * Every loaded model, mirrored out of `FragmentsManager.list` so React can
     * read it. `ModelsSync` is the only thing that dispatches SET_MODEL_IDS.
     */
    modelIds: string[];
    floorplans: Plan[];
    grid: OBC.SimpleGrid | null;
    /** Gates the point cloud, splat, model and DXF loaders so the BIM model gets the first bytes. */
    fragmentsStarted: boolean;
    // For now, store the file with the building
    buildingModel: {
        bimFile: DbFile | null;
        building: Building | null;
    };
    bimModelsAddedToMap: {
        "bimFile": DbFile;
        building?: Building | null;
    }[];
    editingBimModel: string | null;
    bimModelName: string | null;
    /** Mirror of the Highlighter's selection; only SelectionSync dispatches SET_BIM_SELECTION. */
    selection: OBC.ModelIdMap;
    /** The selected file-backed object, when the last click hit one. Only SelectionSync dispatches it. */
    sceneSelection: { kind: 'object' | 'splat'; fileId: string } | null;
    bcfTopic: OBC.Topic | Partial<OBC.Topic> | null;
    bcfTopics: OBC.Topic[] | Partial<OBC.Topic>[];
    bcfTopicId: string | null;
    modelUIState: Record<number, { isVisible?: boolean; isGhost?: boolean }>;
    /**
     * File ids the user has switched on for the BIM scene. The desired set;
     * `BimPointCloudSync` reconciles `BimPointClouds` against it.
     */
    pointCloudIds: string[];
    /** The cloud the alignment tools act on. */
    activePointCloudId: string | null;
    /**
     * File ids the user has switched on for the BIM scene. The desired set;
     * `BimSplatSync` reconciles `BimSplats` against it.
     */
    splatIds: string[];
}

export type BimState = BimTypes;

export type BimPayload = {
    ["SET_COMPONENTS"]: Pick<BimTypes, "bimComponents" | "world" | "fragments">;
    ["SET_WORLD"]: Pick<BimTypes, "world">;
    ["SET_MODEL"]: Pick<BimTypes, "modelId">;
    ["SET_FLOORPLANS"]: Pick<BimTypes, "floorplans">;
    ["SET_GRID"]: Pick<BimTypes, "grid">;
    ["SET_FRAGMENTS_STARTED"]: Pick<BimTypes, "fragmentsStarted">;
    ["DISPOSE-BIM"]: void;
    ["TOGGLE_BIM_TO_MAP"]: Pick<BimTypes, "buildingModel">;
    ["REMOVE_BIM_FROM_MAP"]: Pick<BimTypes, "bimModelName">;
    ["REMOVE_ALL_BIM_FROM_MAP"]: void;
    ["EDIT_BIM_MODEL_BY_NAME"]: Pick<BimTypes, "editingBimModel">;
    ["ADD_BCF_TOPIC"]: Pick<BimTypes, "bcfTopic">;
    ["REMOVE_BCF_TOPIC"]: Pick<BimTypes, "bcfTopicId">;
    ['EDIT_BCF_TOPIC']: Pick<BimTypes, "bcfTopic">;
    ["SET_MODEL_UI_STATE"]: { fileId: number; isVisible?: boolean; isGhost?: boolean };
    ["SET_POINT_CLOUD_IDS"]: { pointCloudIds: string[] };
    ["SET_BIM_SELECTION"]: Pick<BimTypes, "selection">;
    ["SET_SCENE_SELECTION"]: Pick<BimTypes, "sceneSelection">;
    ["SET_MODEL_IDS"]: Pick<BimTypes, "modelIds">;
    ["TOGGLE_POINT_CLOUD"]: { pointCloudId: string };
    ["SET_ACTIVE_POINT_CLOUD"]: Pick<BimTypes, "activePointCloudId">;
    ["SET_SPLAT_IDS"]: { splatIds: string[] };
    ["TOGGLE_SPLAT"]: { splatId: string };
};

export type BimActions = ActionMap<BimPayload>[keyof ActionMap<BimPayload>];

export const BimReducer = (state: BimState, action: BimActions) => {

    switch (action.type) {

        case "SET_COMPONENTS":
            return {
                ...state,
                "bimComponents": action.payload.bimComponents,
                "world": action.payload.world,
                "fragments": action.payload.fragments,
            };
        case "SET_WORLD":
            return {
                ...state,
                "world": action.payload.world,
            };
        case "SET_MODEL":
            return {
                ...state,
                "modelId": action.payload.modelId,
            };
        case "SET_FLOORPLANS":
            return {
                ...state,
                "floorplans": action.payload.floorplans,
            }
        case "SET_GRID":
            return {
                ...state,
                "grid": action.payload.grid,
            };
        case "SET_FRAGMENTS_STARTED":
            return {
                ...state,
                "fragmentsStarted": action.payload.fragmentsStarted,
            };
        case "SET_BIM_SELECTION":
            return {
                ...state,
                "selection": action.payload.selection,
            };
        case "SET_SCENE_SELECTION":
            return {
                ...state,
                "sceneSelection": action.payload.sceneSelection,
            };
        case "SET_MODEL_IDS":
            return {
                ...state,
                "modelIds": action.payload.modelIds,
            };
        case "DISPOSE-BIM":
            return {
                ...state,
                "bimComponents": null,
                "world": null,
                "fragments": null,
                "modelId": null,
                "modelIds": [],
                "selection": {},
                "sceneSelection": null,
                "pointCloudIds": [],
                "activePointCloudId": null,
                "splatIds": [],
                "fragmentsStarted": false,
            };
        case "SET_POINT_CLOUD_IDS": {
            const { pointCloudIds } = action.payload;
            return {
                ...state,
                pointCloudIds,
                "activePointCloudId": state.activePointCloudId && pointCloudIds.includes(state.activePointCloudId)
                    ? state.activePointCloudId
                    : null,
            };
        }
        case "TOGGLE_POINT_CLOUD": {
            const { pointCloudId } = action.payload;
            const isOn = state.pointCloudIds.includes(pointCloudId);
            return {
                ...state,
                "pointCloudIds": isOn
                    ? state.pointCloudIds.filter((id) => id !== pointCloudId)
                    : [...state.pointCloudIds, pointCloudId],
                "activePointCloudId": isOn && state.activePointCloudId === pointCloudId
                    ? null
                    : state.activePointCloudId,
            };
        }
        case "SET_ACTIVE_POINT_CLOUD":
            return {
                ...state,
                "activePointCloudId": action.payload.activePointCloudId,
            };
        case "SET_SPLAT_IDS":
            return {
                ...state,
                "splatIds": action.payload.splatIds,
            };
        case "TOGGLE_SPLAT": {
            const { splatId } = action.payload;
            return {
                ...state,
                "splatIds": state.splatIds.includes(splatId)
                    ? state.splatIds.filter((id) => id !== splatId)
                    : [...state.splatIds, splatId],
            };
        }
        case "TOGGLE_BIM_TO_MAP": {

            const { bimFile } = action.payload.buildingModel;
            const exists = state.bimModelsAddedToMap.some((model) => model.bimFile.id === bimFile.id
            );
            const bimModelsAddedToMap = exists
                ? state.bimModelsAddedToMap.filter((model) => model.bimFile.id !== bimFile.id,
                ) : [
                    ...state.bimModelsAddedToMap,
                    {
                        bimFile,
                        "building": action.payload.buildingModel.building
                    },
                ];
            return {
                ...state,
                bimModelsAddedToMap
            };

        }
        case "REMOVE_BIM_FROM_MAP": {

            const removeName = action.payload.bimModelName;
            return {
                ...state,
                bimModelsAddedToMap: state.bimModelsAddedToMap.filter((model) => model.bimFile.name !== removeName
                ),
            };

        }
        case "REMOVE_ALL_BIM_FROM_MAP":
            return {
                ...state,
                "bimModelsAddedToMap": [],
            };
        case "EDIT_BIM_MODEL_BY_NAME": {
            return {
                ...state,
                "editingBimModel": action.payload.editingBimModel,
            }
        }
        case "ADD_BCF_TOPIC":
            return {
                ...state,
                "bcfTopics": [...state.bcfTopics, action.payload.bcfTopic],
            };
        case "REMOVE_BCF_TOPIC":
            return {
                ...state,
                "bcfTopics": state.bcfTopics.filter((topic) => topic.guid !== action.payload.bcfTopicId),
            };
        case "EDIT_BCF_TOPIC":
            return {
                ...state,
                "bcfTopics": state.bcfTopics.map((topic) =>
                    topic.guid === action.payload.bcfTopic.guid
                        ? action.payload.bcfTopic
                        : topic
                ),
            };
        case "SET_MODEL_UI_STATE": {
            const { fileId, ...uiProps } = action.payload
            return {
                ...state,
                modelUIState: {
                    ...state.modelUIState,
                    [fileId]: {
                        ...state.modelUIState[fileId],
                        ...uiProps,
                    },
                },
            }
        }
        default:
            return state;

    }

};
